import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowRight, CalendarDays, ChevronLeft, ChevronRight, Download, List, Pencil, Trash2 } from 'lucide-react';
import { appointmentsAPI, contactsAPI } from '../../api/client';
import { useTenant } from '../../context/TenantContext';
import { apiError } from '../../lib/host';
import { ErrorBanner, formatTime, localParts, Modal, PageHeading, saveDownload, StatCards, useBasic } from './ui';

export const STATUS = { scheduled: ['Scheduled', 'pending'], confirmed: ['Confirmed', 'green'], completed: ['Completed', 'blue'], cancelled: ['Cancelled', 'red'] };
export const KINDS = { site_visit: 'Site visit', online_meeting: 'Online meeting', phone_call: 'Phone call', in_person: 'In person', other: 'Other' };
const DURATIONS = [15, 30, 45, 60, 90, 120];
const DAY = 24 * 60 * 60 * 1000;

const startOfWeek = (d) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return new Date(x.getTime() - ((x.getDay() + 6) % 7) * DAY); // Monday
};
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const longDate = (iso) => new Date(`${iso}Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

export const StatusPill = ({ status }) => <span className={`status ${(STATUS[status] || [])[1] || ''}`}>{(STATUS[status] || [status])[0]}</span>;

function AppointmentForm({ initial, contacts, onClose, onSaved }) {
  const inAnHour = new Date(Date.now() + 60 * 60 * 1000);
  inAnHour.setMinutes(0, 0, 0);
  const start = localParts(initial?.starts_at || inAnHour.toISOString().slice(0, 19));
  const [form, setForm] = useState({
    title: initial?.title || '', date: start.date, time: start.time, duration_minutes: initial?.duration_minutes || 30,
    contact_id: initial?.contact_id ? String(initial.contact_id) : '', kind: initial?.kind || '', location: initial?.location || '',
    status: initial?.status || 'scheduled', notes: initial?.notes || '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    const startsAt = new Date(`${form.date}T${form.time}`);
    if (Number.isNaN(startsAt.getTime())) {
      setError('Choose a valid date and time.');
      return;
    }
    const body = {
      title: form.title.trim(), starts_at: startsAt.toISOString(), duration_minutes: Number(form.duration_minutes),
      contact_id: form.contact_id ? Number(form.contact_id) : null, kind: form.kind || null, location: form.location,
      status: form.status, notes: form.notes,
    };
    setSaving(true);
    setError('');
    try {
      const res = initial?.id ? await appointmentsAPI.update(initial.id, body) : await appointmentsAPI.create(body);
      onSaved(res.data, initial?.id ? 'updated' : 'booked');
    } catch (err) {
      setError(apiError(err, 'Could not save the appointment'));
      setSaving(false);
    }
  };

  return (
    <Modal
      title={initial?.id ? `Edit ${initial.title}` : 'Book appointment'}
      onClose={onClose}
      footer={<>
        <button type="button" className="secondary-action" onClick={onClose}>Cancel</button>
        <button type="submit" form="appointment-form" className="primary-action" disabled={saving}>{saving ? 'Saving…' : initial?.id ? 'Save changes' : 'Book appointment'}</button>
      </>}
    >
      <form id="appointment-form" className="form-grid" onSubmit={save}>
        <ErrorBanner message={error} />
        <label className="field span-2">Title *<input required maxLength={255} value={form.title} onChange={set('title')} placeholder="Site visit - 3 BHK" autoFocus /></label>
        <label className="field">Date *<input type="date" required value={form.date} onChange={set('date')} /></label>
        <label className="field">Time *<input type="time" required value={form.time} onChange={set('time')} /></label>
        <label className="field">Duration
          <select value={form.duration_minutes} onChange={set('duration_minutes')}>
            {DURATIONS.map((m) => <option key={m} value={m}>{m < 60 ? `${m} min` : `${m / 60} hour${m > 60 ? 's' : ''}`}</option>)}
          </select>
        </label>
        <label className="field">Contact
          <select value={form.contact_id} onChange={set('contact_id')}>
            <option value="">— No contact —</option>
            {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}{c.phone ? ` · ${c.phone}` : ''}</option>)}
          </select>
        </label>
        <label className="field">Type
          <select value={form.kind} onChange={set('kind')}>
            <option value="">—</option>
            {Object.entries(KINDS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="field">Status
          <select value={form.status} onChange={set('status')}>{Object.entries(STATUS).map(([v, [l]]) => <option key={v} value={v}>{l}</option>)}</select>
        </label>
        <label className="field span-2">Location or meeting link<input maxLength={255} value={form.location} onChange={set('location')} placeholder="Office, site address or video link" /></label>
        <label className="field span-2">Notes<textarea rows={3} maxLength={5000} value={form.notes} onChange={set('notes')} /></label>
      </form>
    </Modal>
  );
}

function WeekCalendar({ weekStart, items, onPrev, onNext, onToday, onOpen }) {
  const days = Array.from({ length: 7 }, (_, i) => new Date(weekStart.getTime() + i * DAY));
  const today = new Date();
  const label = `${days[0].toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – ${days[6].toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  return (
    <section className="panel calendar-panel">
      <div className="panel-heading">
        <h2>{label}</h2>
        <div className="calendar-nav">
          <button className="row-action" onClick={onPrev} aria-label="Previous week"><ChevronLeft size={13} /></button>
          <button className="row-action" onClick={onToday}>This week</button>
          <button className="row-action" onClick={onNext} aria-label="Next week"><ChevronRight size={13} /></button>
        </div>
      </div>
      <div className="week-grid">
        {days.map((d) => {
          const dayItems = items.filter((a) => sameDay(new Date(`${a.starts_at}Z`), d));
          return (
            <div key={d.toISOString()} className={`week-day ${sameDay(d, today) ? 'today' : ''}`}>
              <div className="week-day-head"><small>{d.toLocaleDateString('en-GB', { weekday: 'short' })}</small><b>{d.getDate()}</b></div>
              {dayItems.map((a) => (
                <button key={a.id} className={`week-item ${(STATUS[a.status] || [])[1] || ''}`} onClick={() => onOpen(a)}>
                  <small>{formatTime(a.starts_at)}</small><b>{a.title}</b>{a.contact_name && <span>{a.contact_name}</span>}
                </button>
              ))}
              {dayItems.length === 0 && <span className="week-empty">—</span>}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function AppointmentsPage() {
  const { notify, upgrade, plan } = useBasic();
  const { can } = useTenant();
  const canWrite = can('appointments.write');
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState(params.get('view') === 'calendar' ? 'calendar' : 'list');
  const [range, setRange] = useState('upcoming');
  const [status, setStatus] = useState('');
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');

  const query = useMemo(() => {
    if (view === 'calendar') return { start: weekStart.toISOString(), end: new Date(weekStart.getTime() + 7 * DAY).toISOString() };
    const now = new Date().toISOString();
    return { ...(range === 'upcoming' && { start: now }), ...(range === 'past' && { end: now }), ...(status && { status }) };
  }, [view, weekStart, range, status]);

  const load = useCallback(() => {
    appointmentsAPI.list(query).then((res) => {
      setItems(range === 'past' && view === 'list' ? [...res.data.items].reverse() : res.data.items);
      setError('');
    }).catch((err) => setError(apiError(err, 'Could not load appointments')));
    appointmentsAPI.stats().then((res) => setStats(res.data)).catch(() => {});
  }, [query, range, view]);

  useEffect(load, [load]);
  useEffect(() => { contactsAPI.list({ limit: 500 }).then((res) => setContacts(res.data.items)).catch(() => {}); }, []);
  useEffect(() => {
    if (params.get('new') && canWrite) setEditing({});
    if (params.get('new') || params.get('view')) setParams({}, { replace: true });
  }, [params, setParams, canWrite]);

  const saved = (appt, verb) => { setEditing(null); notify(`${appt.title} ${verb}.`); load(); };

  const setStatusOf = async (appt, next, verb) => {
    try {
      await appointmentsAPI.update(appt.id, { status: next });
      notify(`${appt.title} ${verb}.`);
      load();
    } catch (err) { setError(apiError(err)); }
  };

  const remove = async (appt) => {
    if (!window.confirm(`Delete ${appt.title}? This can't be undone.`)) return;
    try {
      await appointmentsAPI.remove(appt.id);
      notify(`${appt.title} deleted.`);
      load();
    } catch (err) { setError(apiError(err)); }
  };

  const exportCsv = async () => {
    try {
      saveDownload(await appointmentsAPI.exportCsv(view === 'calendar' ? query : { ...(status && { status }) }), 'appointments');
      notify('Schedule exported.');
    } catch (err) { setError(apiError(err, 'Could not export the schedule')); }
  };

  const showCalendar = () => setView('calendar');

  return (
    <section className="page-view">
      <PageHeading title="Appointments" subtitle="Track meetings and bookings with your customers.">
        <button className="secondary-action" onClick={exportCsv}><Download size={14} />Export</button>
        {canWrite && <button className="primary-action" onClick={() => setEditing({})}><span>＋</span>Book appointment</button>}
      </PageHeading>
      <ErrorBanner message={error} />

      <StatCards stats={[
        ['Upcoming', stats?.upcoming_7_days, 'Next 7 days'],
        ['Confirmed', stats?.confirmed_upcoming, 'Upcoming'],
        ['Awaiting confirmation', stats?.awaiting_confirmation, 'Upcoming'],
        ['Completed', stats?.completed_this_month, 'This month'],
      ]} />

      <div className="page-toolbar">
        <div className="view-switch" role="tablist">
          <button role="tab" aria-selected={view === 'list'} className={view === 'list' ? 'selected' : ''} onClick={() => setView('list')}><List size={14} />List</button>
          <button role="tab" aria-selected={view === 'calendar'} className={view === 'calendar' ? 'selected' : ''} onClick={showCalendar}><CalendarDays size={14} />Calendar</button>
        </div>
        {view === 'list' && (
          <div className="toolbar-right">
            <select value={range} onChange={(e) => setRange(e.target.value)} aria-label="Time range">
              <option value="upcoming">Upcoming</option><option value="past">Past</option><option value="all">All</option>
            </select>
            <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
              <option value="">All statuses</option>
              {Object.entries(STATUS).map(([v, [l]]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        )}
      </div>

      {view === 'calendar' ? (
        <WeekCalendar weekStart={weekStart} items={items || []} onOpen={(a) => canWrite && setEditing(a)}
          onPrev={() => setWeekStart(new Date(weekStart.getTime() - 7 * DAY))}
          onNext={() => setWeekStart(new Date(weekStart.getTime() + 7 * DAY))}
          onToday={() => setWeekStart(startOfWeek(new Date()))} />
      ) : (
        <section className="panel page-table-panel">
          <div className="panel-heading">
            <div><h2>{range === 'upcoming' ? 'Upcoming appointments' : range === 'past' ? 'Past appointments' : 'All appointments'}</h2>
              <small className="table-subtitle">{items === null ? 'Loading…' : `${items.length} appointment${items.length === 1 ? '' : 's'}`}</small></div>
          </div>
          <div className="table-scroll">
            <table className="full-page-table">
              <thead><tr><th>Date</th><th>Time</th><th>Appointment</th><th>Contact</th><th>Type</th><th>Status</th>{canWrite && <th>Action</th>}</tr></thead>
              <tbody>
                {items?.map((a) => {
                  const open = a.status === 'scheduled' || a.status === 'confirmed';
                  return (
                    <tr key={a.id}>
                      <td>{longDate(a.starts_at)}</td>
                      <td>{formatTime(a.starts_at)} <small className="cell-note inline">{a.duration_minutes} min</small></td>
                      <td><b className="cell-strong">{a.title}</b>{(a.location || a.notes) && <small className="cell-note">{a.location || a.notes}</small>}</td>
                      <td>{a.contact_name || '—'}</td>
                      <td>{KINDS[a.kind] || '—'}</td>
                      <td><StatusPill status={a.status} /></td>
                      {canWrite && (
                        <td>
                          <div className="row-actions">
                            {a.status === 'scheduled' && <button className="row-action" onClick={() => setStatusOf(a, 'confirmed', 'confirmed')}>Confirm</button>}
                            {open && <button className="row-action" onClick={() => setStatusOf(a, 'completed', 'marked as completed')}>Complete</button>}
                            {open && <button className="row-action danger" onClick={() => setStatusOf(a, 'cancelled', 'cancelled')}>Cancel</button>}
                            <button className="row-action" onClick={() => setEditing(a)} aria-label={`Edit ${a.title}`}><Pencil size={12} /></button>
                            <button className="row-action danger" onClick={() => remove(a)} aria-label={`Delete ${a.title}`}><Trash2 size={12} /></button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {items?.length === 0 && (
                  <tr><td colSpan={canWrite ? 7 : 6} className="empty-state">
                    No {range === 'all' ? '' : range} appointments.{canWrite && range !== 'past' && <> <button className="link-button" onClick={() => setEditing({})}>Book one now</button></>}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div className="page-bottom-grid">
        <section className="panel page-info-card">
          <div className="panel-heading"><h2>Quick actions</h2></div>
          <div className="page-action-list">
            {canWrite && (
              <button onClick={() => setEditing({})}>
                <span className="action-dot dot-0">＋</span><span><b>Book appointment</b><small>Schedule a meeting or visit</small></span><ArrowRight size={14} />
              </button>
            )}
            <button onClick={showCalendar}>
              <span className="action-dot dot-1">▦</span><span><b>View calendar</b><small>See the week at a glance</small></span><ArrowRight size={14} />
            </button>
            <button onClick={exportCsv}>
              <span className="action-dot dot-2">↓</span><span><b>Export schedule</b><small>Download a CSV of appointments</small></span><ArrowRight size={14} />
            </button>
          </div>
        </section>
        <section className="panel page-info-card">
          <div className="panel-heading"><h2>Plan access</h2><span className="active-pill"><i />{plan}</span></div>
          <p className="access-copy">Appointments are stored in your company's own database. Times are shown in your local time zone.</p>
          <button className="upgrade-full" onClick={upgrade}>Review subscription <ArrowRight size={14} /></button>
        </section>
      </div>

      {editing && <AppointmentForm initial={editing.id ? editing : null} contacts={contacts} onClose={() => setEditing(null)} onSaved={saved} />}
    </section>
  );
}
